// Central catalogue of derived-question formula types and their display names.
//
// ONE SOURCE OF TRUTH. Three consumers read this module:
//   - QuestionBuilder      — the admin formula dropdown (grouped)
//   - DealRecordPage       — the badge on a derived result (short label)
//   - thresholdAnalysis    — analytics labels (full label)
// Before this module existed the dropdown owned the only value->label mapping
// and the other two rendered the raw formula_type code, so a reader saw
// "CAGR_USD" where the admin saw a sentence. Add a formula HERE and every
// surface picks it up.
//
// `value` is the string PERSISTED as question.formula_type and matched by the
// scoring engine (functions/index.js, functions/formulaLibrary.js). Never
// change a `value` without a data migration — the labels beside it are display
// text only and are safe to reword.
//
// THE THREE CAGRs. CAGR is the reporting-currency growth rate; CAGR_USD is the
// PAST USD growth, converting each historical year at that year's actual
// December year-end rate; CAGR_USD_FUTURE is the FORWARD USD growth over the
// company's PROJECTED revenues, converted at PROJECTED year-end rates (the base
// rate depreciated per year). All three format identically (formatComputedValue
// never sees formula_type), so the labels are the only thing keeping a reader
// from treating one as another — a past USD growth read as future, or a USD
// growth read as reporting-currency, is a real misreading. Keep them visibly
// distinct: no label may be a prefix of another (a truncating UI must not render
// one as another) and each USD label must say whether it is past or future.

export const FORMULA_GROUPS = [
  {
    label: 'Growth',
    formulas: [
      {
        value: 'CAGR',
        label: 'CAGR (reporting currency)',
        short: 'CAGR (reporting)',
      },
      {
        // DISPLAY TEXT ONLY — the stored formula_type string stays "CAGR_USD".
        // Relabelled "Past" to distinguish it from CAGR_USD_FUTURE below.
        value: 'CAGR_USD',
        label: 'CAGR - Past (USD, actual year-end FX)',
        short: 'CAGR (USD past)',
      },
      {
        value: 'CAGR_USD_FUTURE',
        label: 'CAGR - Future (USD, projected FX)',
        short: 'CAGR (USD future)',
      },
      { value: 'GROWTH_RATE', label: 'Period-over-Period Growth Rate' },
      { value: 'AVERAGE', label: 'Average of a Series' },
    ],
  },
  {
    label: 'Profitability',
    formulas: [
      { value: 'EBITDA_MARGIN', label: 'EBITDA Margin' },
      { value: 'NET_MARGIN', label: 'Net Profit Margin' },
    ],
  },
  {
    label: 'Coverage & Liquidity',
    formulas: [
      { value: 'DSCR', label: 'Debt Service Coverage Ratio (DSCR)' },
      { value: 'CURRENT_RATIO', label: 'Current Ratio' },
      { value: 'ASSET_COVERAGE_RATIO', label: 'Asset Coverage Ratio' },
    ],
  },
  {
    label: 'Leverage',
    formulas: [
      { value: 'LEVERAGE', label: 'Debt Multiple (Leverage)' },
      { value: 'DEBT_TO_EQUITY', label: 'Debt to Equity' },
    ],
  },
  {
    label: 'Efficiency',
    formulas: [
      { value: 'REVENUE_PER_EMPLOYEE', label: 'Revenue per Employee' },
      { value: 'CAPITAL_EFFICIENCY', label: 'Capital Efficiency' },
    ],
  },
  {
    label: 'Valuation',
    formulas: [
      { value: 'REVENUE_MULTIPLE', label: 'Revenue Multiple' },
      { value: 'EBITDA_MULTIPLE', label: 'EBITDA Multiple' },
    ],
  },
  {
    label: 'Generic',
    formulas: [
      { value: 'RATIO', label: 'Generic Ratio' },
      { value: 'PERCENTAGE', label: 'Percentage' },
      { value: 'SUM', label: 'Sum' },
      { value: 'GENERAL', label: 'General Formula (a × A [+ − × ÷] b × B)' },
    ],
  },
  {
    label: 'Impact',
    formulas: [
      { value: 'FEMALE_RATIO', label: 'Female Employee Ratio' },
      { value: 'FEMALE_LEADERSHIP_RATIO', label: 'Female Leadership Ratio' },
      { value: 'BENEFICIARY_RATIO', label: 'Beneficiary Ratio' },
      { value: 'IMPACT_REACH_RATIO', label: 'Impact Reach per Capital' },
    ],
  },
];

const BY_VALUE = {};
FORMULA_GROUPS.forEach(group => {
  group.formulas.forEach(f => { BY_VALUE[f.value] = f; });
});

/**
 * Full display name for a formula type, e.g. "CAGR (USD, per-year FX)".
 * Falls back to the raw code so an unrecognised or legacy type still renders
 * something identifiable rather than blank — a stored formula_type that
 * predates this catalogue must never blank out a score breakdown.
 */
export function formulaLabel(formulaType, fallback = 'DERIVED') {
  if (!formulaType) return fallback;
  return BY_VALUE[formulaType]?.label || formulaType;
}

/**
 * Compact display name for tight spaces such as the derived-result badge,
 * e.g. "CAGR (USD)". Entries without an explicit `short` fall back to their
 * full label, then to the raw code.
 */
export function formulaShortLabel(formulaType, fallback = 'DERIVED') {
  if (!formulaType) return fallback;
  const entry = BY_VALUE[formulaType];
  return entry?.short || entry?.label || formulaType;
}
